/**
 * "إضافة طبيب جديد" — the admin's manual doctor creation form.
 *
 * A port of the panel at index.html:1553 and its `handleAddDoctor` (app.js:2384).
 * Same fields in the same order, same red framing, same "صلاحية المدير فقط"
 * badge, same drag-or-click image well.
 *
 * There is no admin create-doctor route, and there never was: the legacy
 * handler posted to `/auth/register` with `role: 'doctor'` and then immediately
 * called `/admin/doctors/:id/approve`, because self-registration creates a
 * doctor as `pending`. That is the path this uses too — two requests, and if
 * the approve half fails the doctor still exists, waiting in طلبات التسجيل,
 * which the error message says rather than leaving the admin guessing.
 */

import { useState } from 'react';
import { Button, Field, Icon, Input, Panel, Select } from '../../components/common';
import { authApi, adminApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useData } from '../../context/DataContext';
import { compressImage } from '../../domain/files';

const EMPTY = {
  name: '',
  title: '',
  categoryId: '',
  priceExam: '',
  priceFollowup: '',
  username: '',
  password: '',
};

const MIN_PASSWORD = 6;

export function AddDoctorForm({ onCreated }) {
  const { categories } = useData();
  const { success, error: toastError } = useNotifications();

  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState('');
  const [imageName, setImageName] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const readImage = async (file) => {
    if (!file) return;
    try {
      setImage(await compressImage(file, { maxWidth: 512, quality: 0.8 }));
      setImageName(file.name);
    } catch (err) {
      toastError(`تعذر قراءة الصورة: ${err.message}`);
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'اسم الطبيب مطلوب';
    if (!form.title.trim()) next.title = 'اللقب المهني مطلوب';
    if (!form.categoryId) next.categoryId = 'اختر التخصص';
    if (!form.priceExam || Number(form.priceExam) <= 0) next.priceExam = 'أدخل سعر كشف صحيح';
    if (!form.priceFollowup || Number(form.priceFollowup) <= 0) {
      next.priceFollowup = 'أدخل سعر متابعة صحيح';
    }
    if (!form.username.trim()) next.username = 'اسم المستخدم مطلوب';
    if (!form.password) next.password = 'كلمة المرور مطلوبة';
    else if (form.password.length < MIN_PASSWORD) {
      next.password = `كلمة المرور يجب أن تكون ${MIN_PASSWORD} أحرف على الأقل`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const username = form.username.trim();
      const result = await authApi.register({
        name: form.name.trim(),
        username,
        password: form.password,
        role: 'doctor',
        // The backend requires both, and an admin creating an account on a
        // doctor's behalf does not have them. The doctor sets their own from
        // الإعدادات once they sign in.
        email: `${username}@capsule.com`,
        phone: '01000000000',
        title: form.title.trim(),
        categoryId: form.categoryId,
        priceExam: Number(form.priceExam),
        priceFollowup: Number(form.priceFollowup),
        image: image || undefined,
      });

      const newId = result?.user?.id;

      // Self-registration lands as "pending"; an admin adding someone directly
      // means them to be live, so approve straight away.
      if (newId) {
        try {
          await adminApi.approveDoctor(newId);
        } catch {
          toastError(
            `تم إنشاء حساب ${form.name.trim()} لكن تعذر تفعيله. فعّله يدوياً من طلبات التسجيل.`,
          );
          setForm(EMPTY);
          setImage('');
          setImageName('');
          onCreated?.();
          return;
        }
      }

      success(`تم إضافة ${form.name.trim()} للمنصة`);
      setForm(EMPTY);
      setImage('');
      setImageName('');
      setErrors({});
      onCreated?.();
    } catch (err) {
      // 409 is the backend's "username taken", which is the mistake an admin
      // is most likely to make here.
      if (err.status === 409) {
        setErrors({ username: 'اسم المستخدم مستخدم بالفعل، اختر اسماً آخر' });
      } else {
        toastError(err.message || 'تعذر إضافة الطبيب');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel className="border-r-4 border-r-red-500">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xl font-bold text-white">
          <Icon name="user-plus" size={24} className="text-red-400" />
          إضافة طبيب جديد
        </h3>
        <span className="rounded bg-red-500/20 px-2 py-1 text-xs text-red-400">
          صلاحية المدير فقط
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Field label="اسم الطبيب" error={errors.name} required>
            <Input value={form.name} onChange={update('name')} placeholder="اسم الطبيب" />
          </Field>

          <Field label="اللقب المهني" error={errors.title} required>
            <Input value={form.title} onChange={update('title')} placeholder="استشاري الباطنة" />
          </Field>

          <Field label="التخصص" error={errors.categoryId} required>
            <Select value={form.categoryId} onChange={update('categoryId')}>
              <option value="">اختر التخصص...</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="سعر الكشف (ج.م)" error={errors.priceExam} required>
            <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
          </Field>

          <Field label="سعر المتابعة (ج.م)" error={errors.priceFollowup} required>
            <Input
              type="number"
              min="0"
              value={form.priceFollowup}
              onChange={update('priceFollowup')}
            />
          </Field>

          <Field label="صورة الطبيب" hint="اختيارية">
            <label
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                readImage(event.dataTransfer.files?.[0]);
              }}
              className={`flex min-h-[48px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-3 text-sm transition-all ${
                dragging
                  ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400'
                  : 'border-white/20 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400'
              }`}
            >
              {image ? (
                <>
                  <img
                    src={image}
                    alt=""
                    className="h-8 w-8 rounded-lg border-2 border-cyan-500 object-cover"
                  />
                  <span className="truncate">{imageName || 'تم اختيار صورة'}</span>
                </>
              ) : (
                <>
                  <Icon name="image-plus" size={20} />
                  <span>اسحب صورة أو اضغط للاختيار</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => readImage(event.target.files?.[0])}
              />
            </label>
          </Field>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold text-cyan-400">
            <Icon name="key-round" size={14} />
            بيانات دخول الطبيب
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="اسم المستخدم" error={errors.username} required>
              <Input
                value={form.username}
                onChange={update('username')}
                autoComplete="off"
                dir="ltr"
                className="font-mono text-sm"
                placeholder="dr_username"
              />
            </Field>

            <Field
              label="كلمة المرور"
              error={errors.password}
              hint={`${MIN_PASSWORD} أحرف على الأقل`}
              required
            >
              <Input
                value={form.password}
                onChange={update('password')}
                autoComplete="new-password"
                dir="ltr"
                className="font-mono text-sm"
              />
            </Field>
          </div>

          <p className="mt-3 text-[11px] text-slate-500">
            يُنشأ الحساب مفعّلاً مباشرة. سلّم هذه البيانات للطبيب ليغيّر كلمة المرور والبريد
            من صفحة الإعدادات.
          </p>
        </div>

        <Button
          type="submit"
          variant="danger"
          icon="plus"
          loading={submitting}
          className="w-full py-3"
        >
          إضافة للمنصة
        </Button>
      </form>
    </Panel>
  );
}

export default AddDoctorForm;
